import React, { useCallback, useState } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Card, Stack, Button, Divider, TextField, Container, Typography, CircularProgress } from "@mui/material"

import { getAuth, updateProfile } from "firebase/auth"
import { auth, db } from "@/config/firebase"
import { doc, setDoc } from "firebase/firestore"
import { useNavigate } from "react-router-dom"
import LogoutConfirmDialog from "@/shared/components/LogoutConfirmDialog"

const SignUpSchema = Yup.object().shape({
  full_name: Yup.string().required("Name is required"),
  contact_number: Yup.string().required("Contact number is required")
})

interface Props {
  onUpdated: (data: any) => void
}

const UserDetails: React.FC<Props> = ({ onUpdated }) => {
  const [loading, setLoading] = useState(false)
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      full_name: "",
      contact_number: ""
    },
    validationSchema: SignUpSchema,
    onSubmit: async values => {
      try {
        setLoading(true)
        const user = getAuth().currentUser
        if (user) {
          await updateProfile(user, { displayName: values.full_name }).catch(err => {
            console.error(err)
          })
          await setDoc(
            doc(db, "users", user.uid),
            {
              email: user.email ?? "",
              ...values
            },
            { merge: true }
          )
          onUpdated(values)
        }
      } catch (error) {
        console.error("User authentication failed:", error)
      } finally {
        setLoading(false)
      }
    }
  })

  const openLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(true)
  }, [])

  const closeLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(false)
  }, [])

  const onLogout = useCallback(() => {
    auth.signOut().then(() => {
      navigate("/")
    })
  }, [navigate])

  return (
    <Container maxWidth="sm">
      <Card variant="outlined" sx={{ p: 4, mt: 8 }}>
        <Stack spacing={4} component="form" onSubmit={formik.handleSubmit}>
          <Typography variant="h5" component="h1" gutterBottom>
            Complete Profile
          </Typography>
          <TextField
            fullWidth
            id="full_name"
            name="full_name"
            label="Name"
            size="small"
            margin="normal"
            value={formik.values.full_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.full_name && Boolean(formik.errors.full_name)}
            helperText={formik.touched.full_name && formik.errors.full_name}
          />
          <TextField
            fullWidth
            id="contact_number"
            name="contact_number"
            label="Contact Number"
            size="small"
            margin="normal"
            value={formik.values.contact_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
            helperText={formik.touched.contact_number && formik.errors.contact_number}
          />

          <Button
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            color="primary"
            variant="contained"
            fullWidth
            type="submit">
            Submit
          </Button>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Button onClick={openLogoutConfirm} color="secondary" variant="outlined">
            Logout
          </Button>
        </Stack>
      </Card>
      <LogoutConfirmDialog open={logoutConfirmOpen} onClose={closeLogoutConfirm} onConfirm={onLogout} />
    </Container>
  )
}

export default UserDetails
